import React, { FC } from 'react';

import { Box, Center, Grid, GridItem, HStack, Stack } from '@chakra-ui/react';
import { join } from 'lodash';
import map from 'lodash/map';
import { customTheme } from 'theme';

import { getIcon, WithResourceIcon } from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { MetadataCommonFields } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataCommonFields';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';

import {
  CloudFrontAliases,
  CloudFrontLogging,
  DefaultRoot,
  DomainName,
  GeoRestrictions,
  HTTPVersion,
  IPv6Enabled,
  LastModified,
  OriginGroupEntities,
  OriginGroupID,
  OriginGroups,
  RestrictedGeos,
  RestrictionType,
  WAFACLID,
} from '../../../../Components/MetadataField/MetaGridFields';

export const CloudFrontMetadata: FC = () => {
  const { resourceDetails } = useActiveResourceContext();
  const { data: resource } = resourceDetails;

  const configuration = resource?.config?.Origins?.Items ?? [];
  const cacheBehaviors = resource?.config?.CacheBehaviors?.Items ?? [];

  const renderValueWithIcon = value =>
    !value ? (
      '-'
    ) : (
      <HStack spacing={1}>
        <Box boxSize={5}>
          <Center>{getIcon(value)}</Center>
        </Box>
        <Box>{value}</Box>
      </HStack>
    );

  const originFields = [
    { label: 'ID', value: 'Id' },
    {
      label: 'Type',
      render: row =>
        !!row?.S3OriginConfig
          ? 'S3'
          : !!row?.VpcOriginConfig
          ? 'VPC'
          : 'Custom',
    },
    { label: 'Domain Name', value: 'DomainName' },
    {
      label: 'Access identity ID',
      render: row =>
        row?.S3OriginConfig?.OriginAccessIdentity ||
        row?.VpcOriginConfig?.VpcOriginId ||
        '-',
    },
    { label: 'Restricted access', value: '' },
    {
      label: 'Ports',
      render: row =>
        !!row?.CustomOriginConfig ? (
          <Box>
            http({row?.CustomOriginConfig?.HTTPPort}), https(
            {row?.CustomOriginConfig?.HTTPSPort})
          </Box>
        ) : (
          '-'
        ),
    },
    {
      label: 'SSL protocols',
      render: row => {
        const protocols = row?.CustomOriginConfig?.OriginSslProtocols?.Items;
        return !!row?.CustomOriginConfig ? (
          <HStack>
            {map(protocols, (o: string, index: number) => (
              <HStack key={o + index} spacing={0}>
                {renderValueWithIcon(o)}
                {index !== protocols?.length - 1 && <Box>, </Box>}
              </HStack>
            ))}
          </HStack>
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Origin protocol',
      render: row =>
        renderValueWithIcon(row?.CustomOriginConfig?.OriginProtocolPolicy),
    },
    {
      label: 'Origin shield use ',
      render: row => (row?.OriginShield?.Enabled ? 'Yes' : 'No'),
    },
    {
      label: 'Origin shield region ',
      render: row => row?.OriginShield?.OriginShieldRegion || '-',
    },
    {
      label: 'Origin access control ID ',
      render: row => row?.OriginAccessControlId || '-',
    },
  ];

  const cacheBehaviorFields = [
    {
      label: 'Target origin ID',
      render: row => row?.TargetOriginId,
    },
    {
      label: 'Path pattern',
      render: row => row?.PathPattern ?? '-',
    },
    {
      label: 'Viewer protocol',
      render: row => renderValueWithIcon(row?.ViewerProtocolPolicy),
    },
    {
      label: 'Compress',
      render: row => (row?.Compress ? 'Yes' : 'No'),
    },
    {
      label: 'Allowed methods',
      render: row =>
        !!row?.AllowedMethods?.Quantity
          ? join(row?.AllowedMethods?.Items, ', ')
          : '-',
    },
    {
      label: 'Cached methods',
      render: row =>
        !!row?.AllowedMethods?.CachedMethods?.Quantity
          ? join(row?.AllowedMethods?.CachedMethods?.Items, ', ')
          : '-',
    },
    {
      label: 'Trusted signers',
      render: row =>
        !!row?.TrustedSigners?.Quantity
          ? join(row?.TrustedSigners?.Items, ', ')
          : '-',
    },
    {
      label: 'Trusted key groups',
      render: row =>
        !!row?.TrustedKeyGroups?.Quantity
          ? join(row?.TrustedKeyGroups?.Items, ', ')
          : '-',
    },
    {
      label: 'Lambda edge functions',
      render: row =>
        row?.LambdaFunctionAssociations?.Quantity
          ? join(
              map(
                row?.LambdaFunctionAssociations?.Items,
                (o: Record<string, any>) => o?.LambdaFunctionARN,
              ),
              ', ',
            )
          : '-',
    },
  ];

  const RenderBox = ({ children }) => (
    <Box
      border="1px solid"
      borderColor="componentBorder"
      px={6}
      py={4}
      w="full"
      borderRadius={6}
    >
      {children}
    </Box>
  );

  const renderOrigin = (item, list) => {
    return (
      <RenderBox>
        <Stack>
          <Grid templateColumns="repeat(4, 1fr)" gap={4}>
            {map(list, field => (
              <React.Fragment key={field.label}>
                <GridItem
                  colSpan={1}
                  color={customTheme.colors.gray['300']}
                  fontWeight={600}
                >
                  {field.label}
                </GridItem>
                <GridItem colSpan={3}>
                  {!!field?.value
                    ? item?.[field?.value]
                    : field?.render?.(item)}
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        </Stack>
      </RenderBox>
    );
  };

  return (
    <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
      <MetadataCommonFields />
      {LastModified(resource)}
      {DomainName(resource)}
      {HTTPVersion(resource)}
      {IPv6Enabled(resource)}
      {CloudFrontLogging(resource)}
      {WAFACLID(resource)}
      {DefaultRoot(resource)}
      {CloudFrontAliases(resource)}
      {OriginGroups(resource)}
      {OriginGroupID(resource)}
      {OriginGroupEntities(resource)}
      {GeoRestrictions(resource)}
      {RestrictionType(resource)}
      {RestrictedGeos(resource)}

      <GridItem colSpan={4}>
        <Stack w="full" spacing={3}>
          <WithResourceIcon resourceType={IconTypes.OriginConfigurations}>
            <Box fontWeight={600}>
              {'Origin Configurations: ' + configuration?.length}
            </Box>
          </WithResourceIcon>
          <Stack w="full" spacing={4}>
            {map(configuration, (o, index) => (
              <React.Fragment key={index}>
                {renderOrigin(o, originFields)}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </GridItem>

      <GridItem colSpan={4} pt={4}>
        <Stack w="full" spacing={3}>
          <WithResourceIcon resourceType={IconTypes.CacheBehaviour}>
            <Box fontWeight={600}>{'Default Cache Behavior'}</Box>
          </WithResourceIcon>
          {renderOrigin(
            resource?.config?.DefaultCacheBehavior,
            cacheBehaviorFields,
          )}
        </Stack>
      </GridItem>

      <GridItem colSpan={4} pt={4}>
        <Stack w="full" spacing={3}>
          <WithResourceIcon resourceType={IconTypes.CacheBehaviour}>
            <Box fontWeight={600}>
              {'Cache Behaviors: ' + cacheBehaviors?.length}
            </Box>
          </WithResourceIcon>
          <Stack w="full" spacing={4}>
            {map(cacheBehaviors, (o, index) => (
              <React.Fragment key={index}>
                {renderOrigin(o, cacheBehaviorFields)}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </GridItem>
    </Grid>
  );
};
